// CSV選択出力によりCSVをダウンロードするためのロジックをまとめたHooks
import { toast } from 'react-hot-toast';
import { useMutation, useQuery } from '@tanstack/react-query';

import { components } from '../../schema/data-crud';
import { datacrudApiClient as client } from '../../utils/apiClient';
import { useSelectedOrCurrentYear } from '../useYears';
import { CustomToast } from './CustomToast';

// -----------------------------------------------------------------------------
// CRUD Operation Functions
// -----------------------------------------------------------------------------

// CSV選択出力のジョブをリクエストする
const postSelectiveCsvRequest = async (
  request: components['schemas']['SelectiveCsvCreateRequest']
): Promise<components['schemas']['SelectiveCsvCreateResponse']> => {
  const { data, error } = await client.POST('/selective-csv/csv', {
    body: request,
  });

  if (error) {
    throw new Error(`Failed to POST /selective-csv/csv \n message: ${error.message}`);
  }

  return data;
};

// CSV選択出力のジョブ一覧を取得する
const getSelectiveCsvRequestList = async (
  year: string
): Promise<components['schemas']['SelectiveCsvGetJobListResponse']> => {
  const { data, error } = await client.GET('/selective-csv/csv', {
    params: {
      query: {
        year,
      },
    },
  });

  if (error) {
    throw new Error(`Failed to GET /selective-csv/csv \n message: ${error.message}`);
  }

  return data;
};

// CSV選択出力のダウンロードURLを取得する
const getSelectiveCsvDownloadUrl = async (
  requestId: string
): Promise<components['schemas']['SelectiveCsvGetDownloadUrlResponse']> => {
  const { data, error } = await client.GET('/selective-csv/csv/{requestId}', {
    params: {
      path: {
        requestId,
      },
    },
  });

  if (error) {
    throw new Error(`Failed to GET /selective-csv/csv/{requestId} \n message: ${error.message}`);
  }

  return data;
};

// -----------------------------------------------------------------------------
// Hooks
// -----------------------------------------------------------------------------

// CSV選択出力のリクエストを処理する
export const useSelectiveCsvRequest = () => {
  const { mutateAsync, isLoading } = useMutation({
    mutationFn: postSelectiveCsvRequest,
    onMutate: () => {
      const toastId = toast.loading('CSV出力のリクエストを処理中です...');
      return { toastId };
    },
    onSuccess: (data, _, context) => {
      toast.success(CustomToast, {
        id: context?.toastId,
        duration: 8000,
      });
      console.log(`Accepted oa-csv create request: id=${data.requestId}`);
    },
    onError: (error, _, context) => {
      console.log(error);
      toast.error('CSV出力のリクエストに失敗しました', {
        id: context?.toastId,
      });
    },
  });
  return { request: mutateAsync, isPending: isLoading };
};

// CSV選択出力のジョブ一覧を取得する
export const useSelectiveCsvJobList = () => {
  const year = useSelectedOrCurrentYear();
  const { data } = useQuery({
    queryKey: ['selective-csv'],
    queryFn: async () => {
      return await getSelectiveCsvRequestList(year);
    },
  });
  return data;
};

// CSV選択出力のダウンロードURLを取得しダウンロードを実行する
export const useSelectiveCsvDownload = (requestId: string) => {
  const { mutateAsync, isLoading } = useMutation({
    mutationFn: async () => getSelectiveCsvDownloadUrl(requestId),
    onMutate: () => {
      const toastId = toast.loading('ダウンロードを準備中です...');
      return { toastId };
    },
    onSuccess: (data, _, context) => {
      toast.success('ダウンロードを開始します', {
        id: context?.toastId,
      });
      window.location.href = data.url;
    },
    onError: (error, _, context) => {
      console.log(error);
      toast.error('ダウロードURLの取得に失敗しました', {
        id: context?.toastId,
      });
    },
  });
  return { download: mutateAsync, isPending: isLoading };
};
